// ─── Capture's client for the server routes in @nulife/capture ──────────────
//
// Shape and behaviour are Crafting Comb's `client/api.ts` Capture section,
// parameterised by the app's own transport and the path its server mounted the
// routes at.

import { ApiError, type ApiClient } from "@nulife/app-ui";

export type CaptureFile = {
  id: string; filename: string; mimeType: string; url: string;
  thumbnailUrl: string | null; size: number;
};

export type CaptureItem = {
  id: string; title: string | null; note: string | null;
  /** A saved web address, or null. */
  link: string | null;
  /** From the page itself, fetched once in the background; null until then
   *  or if the page could not be read. */
  linkTitle: string | null;
  linkDescription: string | null;
  /** The page's picture, kept on our side. */
  linkPreview: { url: string; thumbnailUrl: string | null } | null;
  /** True until the server has tried to read the page. */
  linkPreviewPending: boolean;
  tag: string | null;
  createdAt: Date; updatedAt: Date; files: CaptureFile[];
  /** When it was deleted, or null if it has not been. Turned into "N days
   *  left" on the deleted list — a window nobody can see the end of is one
   *  people do not trust. */
  archivedAt: Date | null;
};

export type CaptureSection = { tag: string; tagKey: string; count: number };

/** Somewhere to keep the capture being decided on, so it survives the page being
 *  thrown away (iOS does that to a page while the camera is in front) or closed.
 *  Optional: an app that does not pass one behaves exactly as before. */
export type DraftStore = {
  load(): Promise<File[]>;
  save(files: File[]): Promise<void>;
  clear(): Promise<void>;
};

export function createCaptureClient(transport: Pick<ApiClient, "request" | "post" | "send">, basePath: string) {
  const { request, post, send } = transport;

  const parse = (raw: any): CaptureItem => ({
    ...raw,
    link: raw.link ?? null,
    linkTitle: raw.linkTitle ?? null,
    linkDescription: raw.linkDescription ?? null,
    linkPreview: raw.linkPreview ?? null,
    linkPreviewPending: !!raw.linkPreviewPending,
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
    archivedAt: raw.archivedAt ? new Date(raw.archivedAt) : null,
  });

  async function list(path: string): Promise<{ captures: CaptureItem[]; retentionDays: number }> {
    const raw = await request<{ captures: any[]; retentionDays: number }>(path);
    return { captures: raw.captures.map(parse), retentionDays: raw.retentionDays };
  }

  return {
    /** `tag: null` = Inbox (untagged); a string = that section; omitted = all. */
    getCaptures: (tag?: string | null) =>
      list(`${basePath}${tag === null ? "?inbox=1" : tag ? `?tag=${encodeURIComponent(tag)}` : ""}`),
    getDeletedCaptures: () => list(`${basePath}?deleted=1`),
    getCaptureSections: () => request<CaptureSection[]>(`${basePath}/sections`),
    restoreCapture: (id: string) => post<{ ok: true }>(`${basePath}/${encodeURIComponent(id)}/restore`, {}),
    saveCapture: (id: string, patch: Partial<{ title: string | null; note: string | null; tag: string | null }>) =>
      request<{ ok: true; changed: boolean }>(`${basePath}/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    renameCaptureSection: (tagKey: string, newTag: string) =>
      request<{ ok: true; moved: number }>(`${basePath}/sections/${encodeURIComponent(tagKey)}`, {
        method: "PATCH", body: JSON.stringify({ tag: newTag }),
      }),
    refreshCaptureLink: (id: string) => post<{ ok: true }>(`${basePath}/${encodeURIComponent(id)}/refresh-link`, {}),
    deleteCapture: (id: string) =>
      request<{ ok: true; changed: boolean }>(`${basePath}/${id}`, { method: "DELETE" }),

    /**
     * `clientActionId` is generated by the CALLER, once per capture attempt: a
     * retry after a dropped connection must reuse it so the server treats the
     * retry as "already done" instead of a second capture. Multipart goes
     * through `send()`, which never sets a JSON content-type.
     */
    async uploadCapture(input: {
      title?: string; note?: string; link?: string; tag?: string; files?: File[]; clientActionId: string;
      signal?: AbortSignal;
    }): Promise<CaptureItem & { queued?: boolean; queuedWhy?: string }> {
      const form = new FormData();
      form.set("clientActionId", input.clientActionId);
      if (input.title) form.set("title", input.title);
      if (input.note) form.set("note", input.note);
      if (input.link) form.set("link", input.link);
      if (input.tag) form.set("tag", input.tag);
      for (const f of input.files ?? []) form.append("items", f, f.name);
      const res = await send(basePath, { method: "POST", credentials: "include", body: form, signal: input.signal });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new ApiError(res.status, (body as { error?: string })?.error ?? "Could not save the capture.");
      return parse(body);
    },
  };
}

export type CaptureClient = ReturnType<typeof createCaptureClient>;
